import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: 'ri-music-fill',
    label: 'Live Entertainment',
    title: 'Live Band & Event Entertainment',
    description: 'We provide professional live band services tailored to match the atmosphere and vision of your event. Our musicians are fluent across multiple genres, allowing us to create customized musical experiences for weddings, private events, corporate functions, celebrations, and special occasions.',
    detail: 'From elegant and refined to high energy performances, we ensure a polished and engaging experience for every audience.',
    ideal: 'Weddings, Corporate Events, Celebrations',
    color: 'from-[#B45309] to-brand-yellow-dark',
    accent: 'bg-[#B45309]/10 text-[#B45309]',
  },
  {
    icon: 'ri-piano-line',
    label: 'Intimate Experiences',
    title: 'Private Piano Performance',
    description: 'We offer private piano performances designed to create a sophisticated and memorable atmosphere for intimate settings. Whether you are planning a private dinner, date night, or special gathering, we provide live piano music tailored to your desired mood and style including jazz, contemporary, and more.',
    detail: 'From soft background ambiance to featured performances, Creative Institute delivers a refined musical experience that elevates every moment.',
    ideal: 'Private Dinners, Romantic Evenings, Luxury Experiences',
    color: 'from-brand-purple-dark to-brand-purple',
    accent: 'bg-brand-purple/10 text-brand-purple',
  },
  {
    icon: 'ri-camera-2-line',
    label: 'Media Production',
    title: 'Photography, Videography & Media',
    description: 'We offer high quality photography and video production services designed to capture and elevate your event or brand. Our services include event photography, cinematic video production, event recap videos, social media reels, live streaming services, and content creation for branding and promotion.',
    detail: 'We focus on delivering visually compelling content that tells your story with clarity and impact.',
    ideal: 'Events, Brands, Social Media, Streaming',
    color: 'from-[#065F46] to-[#10B981]',
    accent: 'bg-[#065F46]/10 text-[#065F46]',
  },
  {
    icon: 'ri-equalizer-2-line',
    label: 'Technical Production',
    title: 'Audio, Visual & Technical Production',
    description: 'We provide comprehensive production and technical services designed to ensure a seamless and professional experience in any environment. Our capabilities include live sound setup, mixing, and operation, sound system optimization and troubleshooting, and audio system design and consultation.',
    detail: 'We also specialize in full audio system installations, camera systems, LED video wall integration, and production upgrades for churches, venues, and event spaces.',
    ideal: 'Venues, Churches, Event Spaces',
    color: 'from-brand-blue to-brand-blue-mid',
    accent: 'bg-brand-blue/10 text-brand-blue',
  },
  {
    icon: 'ri-spotlight-line',
    label: 'Live Productions',
    title: 'Stage Production & Creative Support',
    description: 'We provide hands on support for stage plays, live productions, and creative events, helping bring your vision to life with structure and excellence. Our support includes stage play coordination and execution, musical direction and production support, rehearsal guidance and organization, and technical and creative assistance for live performances.',
    detail: 'From concept to execution, we help ensure your production runs smoothly and professionally.',
    ideal: 'Stage Plays, Live Productions, Performances',
    color: 'from-brand-blue-mid to-brand-purple-dark',
    accent: 'bg-brand-blue-mid/10 text-brand-blue-mid',
  },
  {
    icon: 'ri-lightbulb-line',
    label: 'Strategic Consulting',
    title: 'Creative Direction & Consulting',
    description: 'We offer strategic consulting to help organizations and creatives build, refine, and elevate their creative operations. Our services include evaluating and improving existing creative systems, building and structuring teams and departments, staffing and placement for key creative roles, and workflow development and execution planning.',
    detail: 'We do not just advise — we help you build systems that last.',
    ideal: 'Organizations, Creative Departments, Leadership',
    color: 'from-[#B45309] to-[#D97706]',
    accent: 'bg-[#B45309]/10 text-[#B45309]',
  },
];

function PageHero() {
  return (
    <section className="relative min-h-[60vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#3730A3] via-[#5B21B6] to-[#7C3AED]" />
      <div className="absolute inset-0 pointer-events-none select-none z-0">
        <img
          src="https://static.readdy.ai/image/b0ab6c1eb002a8cf89683099ba8ee0be/a9a19e6966780984f72576a4f2fc1316.png"
          alt=""
          className="absolute top-[-30px] right-[-10px] w-[560px] h-auto object-contain opacity-[0.15] mix-blend-screen"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40" />
      <div className="absolute top-20 right-20 w-80 h-80 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-24 pt-40">
        <span className="hero-badge inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Available Off Site
        </span>
        <h1 className="hero-line-1 font-heading text-white text-6xl lg:text-7xl font-black mb-5 max-w-3xl leading-tight">
          Off-Site Services
        </h1>
        <p className="hero-sub font-body text-white/75 text-lg max-w-2xl leading-relaxed mb-3">
          Creative Institute provides professional creative services for organizations, brands, and events — delivering high level execution, strategic planning, and production excellence across a wide range of environments.
        </p>
        <p className="font-body text-white/60 text-base max-w-2xl leading-relaxed">
          Our team is equipped to support everything from intimate gatherings to large scale productions with precision, creativity, and professionalism.
        </p>
      </div>
    </section>
  );
}

function ServiceCard({ svc, idx }: { svc: typeof services[0]; idx: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const isEven = idx % 2 === 0;
  return (
    <div
      ref={ref}
      className={`scroll-hidden delay-100 ${isVisible ? 'scroll-visible' : ''} bg-white rounded-2xl border border-gray-100 overflow-hidden card-lift group`}
    >
      <div className={`h-1.5 bg-gradient-to-r ${svc.color}`} />
      <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0`}>
        <div className="flex-1 p-8 lg:p-10">
          <div className="flex items-center gap-4 mb-5">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${svc.accent} flex-shrink-0`}>
              <i className={`${svc.icon} text-lg`} />
            </div>
            <span className="font-body text-xs font-semibold uppercase tracking-widest text-gray-400">{svc.label}</span>
          </div>
          <h3 className="font-heading text-2xl font-bold text-brand-blue-dark mb-4">{svc.title}</h3>
          <p className="font-body text-gray-500 text-sm leading-relaxed mb-3">{svc.description}</p>
          <p className="font-body text-gray-400 text-sm leading-relaxed italic">{svc.detail}</p>
        </div>
        <div className={`lg:w-64 xl:w-72 flex-shrink-0 bg-gradient-to-br ${svc.color} flex flex-col items-center justify-center p-8 text-white text-center`}>
          <i className={`${svc.icon} text-4xl opacity-30 mb-4`} />
          <p className="font-body text-xs font-semibold uppercase tracking-widest opacity-70 mb-2">Ideal For</p>
          <p className="font-body text-sm font-medium leading-snug opacity-90">{svc.ideal}</p>
        </div>
      </div>
    </div>
  );
}

function ServicesSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-24 bg-gradient-to-b from-[#F5F3FF] via-[#F8F7FF] to-white">
      <div className="max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest mb-4`}>
            What We Offer
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl font-black text-brand-blue-dark`}>
            Services We Bring <span className="gradient-text">To You</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base mt-4 max-w-2xl mx-auto`}>
            Every service is fully customized to fit your vision, budget, and environment. We bring the same standard of excellence — wherever you need it.
          </p>
        </div>

        <div className="flex flex-col gap-8">
          {services.map((svc, idx) => (
            <ServiceCard key={svc.title} svc={svc} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CustomizationBanner() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.3 });
  return (
    <section ref={ref} className="py-16 bg-gradient-to-r from-[#FFFBEB] via-white to-[#FFFBEB]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} rounded-2xl border border-brand-yellow/30 bg-brand-yellow/8 p-10 text-center`}>
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark mx-auto mb-5">
            <i className="ri-sparkling-line text-2xl" />
          </div>
          <h3 className="font-heading text-3xl font-black text-brand-blue-dark mb-4">
            All Services Are Fully Customizable
          </h3>
          <p className="font-body text-gray-600 text-base leading-relaxed max-w-2xl mx-auto">
            Whether for a one time event or a long term partnership, Creative Institute is equipped to deliver excellence at every level. Every engagement is tailored to the specific needs, vision, and environment of each client.
          </p>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.3 });
  return (
    <section ref={ref} className="relative py-28 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] via-[#3730A3] to-[#5B21B6]" />
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9d0313635eac7aeae95d7452c61b4c11.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-10"
        />
      </div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 max-w-screen-md mx-auto px-8 text-center">
        <span className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6`}>
          Let&apos;s Work Together
        </span>
        <h2 className={`scroll-hidden delay-100 ${isVisible ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-white mb-5 leading-tight`}>
          Let&apos;s Bring Your<br />Vision to Life
        </h2>
        <p className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} font-body text-white/70 text-lg mb-10 leading-relaxed`}>
          Ready to elevate your next event, production, or organization? We will work with you directly to customize and structure your experience.
        </p>
        <div className={`scroll-hidden delay-300 ${isVisible ? 'scroll-visible' : ''} flex flex-wrap justify-center gap-4`}>
          <Link to="/contact" className="whitespace-nowrap px-9 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base hover:bg-brand-yellow-light transition-all cursor-pointer">
            Request a Quote
          </Link>
          <Link to="/contact" className="whitespace-nowrap px-9 py-4 rounded-full border-2 border-white/40 text-white font-body font-semibold text-base hover:border-brand-yellow hover:text-brand-yellow transition-all cursor-pointer">
            Book a Free Consultation
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function OffSiteServicesPage() {
  return (
    <main>
      <PageHero />
      <ServicesSection />
      <CustomizationBanner />
      <CTA />
    </main>
  );
}
